// components/AgentProductForm.tsx
import React from "react";
import { UIInput, UIReactSelect } from "components/Inputfields";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";

export type SelectOption = { value: string; label: string };

// Select2 types for agents
type AgentOption = { 
  value: string; 
  label: string; 
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

export type AgentProductFormValues = {
  // add-only
  agentId?: string;
  agentName?: string;
  agentFirstName?: string;
  agentLastName?: string;

  // shared
  name: string;
  sku: string;
  qty: number | string;
  threshold_qty: number | string;
  brand: string;
  category: string;
  item_condition: string;
  length: string;
  height: string;
  width: string;
  weight: string;
  description: string;
  statuss: string;

  // images (UI only)
  images: File[] | null;
};

type Props = {
  isEdit: boolean;
  // keep this 'any' so it aligns with your Inputfields typing and avoids the Formik initialStatus mismatch
  formik: any;
  // options
  agentOptions: SelectOption[];
  loadAgentOptions?: LoadOptions<AgentOption, any, { page: number }>;
  brandOptions: SelectOption[];
  categoryOptions: SelectOption[];
  conditionOptions: SelectOption[];
  statusOptions: SelectOption[];

  // images
  previewFiles: { file: File; url: string }[];
  uploadedFiles: { alt_text: string; url: string }[];
  onAddFiles: (files: File[]) => void;
  onRemovePreview: (index: number) => void;
  onRemoveUploaded: (index: number) => void;

  // actions
  title: string;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
  baseUrl?: string;
  resolveSrc?: (url: string) => string | null;
  
  // team member support
  isTeammember?: boolean;
  parentBrokerageId?: string | null;
  /** When false, SKU is optional in the UI (default true). */
  requireSku?: boolean;
};

const INPUT_BG = "#f5f6f8";
const BORDER = "#e5e7eb";

// Custom dropdown indicator component
const DropdownIndicator = (props: any) => {
  return (
    <div {...props.innerProps}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
    </div>
  );
};

// Custom styles to ensure dropdown indicator is always visible
const customSelectStyles = {
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    display: 'flex !important',
    opacity: 1,
    visibility: 'visible',
  }),
  indicatorSeparator: (provided: any, state: any) => ({
    ...provided,
    display: 'none',
  }),
};

const AgentProductForm: React.FC<Props> = ({
  isEdit,
  formik,
  agentOptions,
  loadAgentOptions,
  brandOptions,
  categoryOptions,
  conditionOptions,
  statusOptions,
  previewFiles,
  uploadedFiles,
  onAddFiles,
  onRemovePreview,
  onRemoveUploaded,
  title,
  onBack,
  onSubmit,
  submitting,
  resolveSrc,
  baseUrl,
  isTeammember,
  parentBrokerageId,
  requireSku = true,
}) => {
  return (
    <div className="content container">
      {/* Page header */}
      <div className=" mb-4">
        <h2 className="m-0 ">{title}</h2>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} noValidate>
        {/* Product Details card */}
        <div className="card border rounded-4 shadow-lg overflow-hidden mb-4">
          <div className="px-4 pt-0 pb-4">
            <div className="d-flex align-items-center mb-5" style={{ padding: "25px 18px", borderBottom: "1px solid #eef2f7" }}>
              <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 26, height: 26 }}>
                <i className="bi bi-box-seam me-2 text-primary" style={{ fontSize: 26, color: "#155dfc", lineHeight: 1 }} />
              </span>
              <div className="fs-19 roboto-new ms-2">Product Details</div>
            </div>
            <div className="row g-4">
              {/* LEFT: Basic Information */}
              <div className="col-lg-6">
                <div className="rounded-4 p-3">
                  <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Basic Information</h5>
                  {!isEdit && (
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Agent <span className="text-danger">*</span>
                        </label>
                        {loadAgentOptions ? (
                          (!isTeammember || parentBrokerageId) ? (
                            <AsyncPaginate
                              classNamePrefix="rs"
                              placeholder="Search agents..."
                              value={formik.values.agentId ? {
                                value: formik.values.agentId,
                                label: formik.values.agentName || agentOptions.find(opt => opt.value === formik.values.agentId)?.label || "Select Agent",
                                firstName: formik.values.agentFirstName || "",
                                lastName: formik.values.agentLastName || "",
                                email: "",
                                phone: ""
                              } : null}
                              loadOptions={loadAgentOptions}
                              onChange={(selected) => {
                                if (selected) {
                                  formik.setFieldValue("agentId", selected.value || "");
                                  formik.setFieldValue("agentName", selected.label || "");
                                  formik.setFieldValue("agentFirstName", selected.firstName || "");
                                  formik.setFieldValue("agentLastName", selected.lastName || "");
                                } else {
                                  formik.setFieldValue("agentId", "");
                                  formik.setFieldValue("agentName", "");
                                  formik.setFieldValue("agentFirstName", "");
                                  formik.setFieldValue("agentLastName", "");
                                }
                              }}
                              onBlur={() => formik.setFieldTouched("agentId", true)}
                              additional={{ page: 1 }}
                              defaultOptions
                              debounceTimeout={300}
                              isClearable
                              components={{ DropdownIndicator }}
                              styles={customSelectStyles}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "38px" }}>
                              <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <span className="text-muted">Loading agents...</span>
                            </div>
                          )
                        ) : (
                          <UIReactSelect
                            formik={formik}
                            name="agentId"
                            label=""
                            required
                            options={agentOptions}
                            placeholder="Select agent"
                          />
                        )}
                        {formik.touched.agentId && formik.errors.agentId && (
                          <div className="text-danger mt-1">{formik.errors.agentId}</div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <UIReactSelect
                        formik={formik as any}
                        name="brand"
                        label="Brand"
                        options={brandOptions}
                        placeholder="Select brand"
                      />
                    </div>
                    <div className="col-md-6">
                      <UIReactSelect
                        formik={formik as any}
                        name="category"
                        label="Category"
                        // required
                        options={categoryOptions}
                        placeholder="Select category"
                      />
                    </div>
                  </div>

                  {/* Images */}
                  <div className="mt-3">
                    <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Image</h5>
                    <div
                      className="border-1 rounded-4 p-4 text-center"
                      style={{
                        borderStyle: "dashed",
                        background: "#fafafa",
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const files = Array.from(e.dataTransfer.files || []);
                        onAddFiles(files as File[]);
                      }}
                    >
                      <div className="mb-2">
                        <i className="bi bi-upload fs-2" />
                      </div>
                      <div className="mb-1">
                        <span className="fw-semibold">Choose files</span> or
                        drag and drop
                      </div>
                      <div className="text-muted small">
                        PNG, JPG up to 10MB each
                      </div>
                      <div className="text-muted small">
                        You can select multiple images at once
                      </div>
                      <div className="mt-3">
                        <label className="btn upload-btn mt-2">
                          Browse…
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            hidden
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              onAddFiles(files as File[]);
                            }}
                          />
                        </label>
                      </div>
                    </div>

                    {/* Previews */}
                    {previewFiles.length > 0 && (
                      <div className="row g-3 mt-3">
                        {previewFiles.map((item, index) => (
                          <div key={`preview-${index}`} className="col-6 col-md-4 col-lg-3">
                            <div className="img-card position-relative">
                              <button
                                type="button"
                                className="btn-close img-x"
                                aria-label="Remove"
                                onClick={() => onRemovePreview(index)}
                              />
                              <img src={item.url} alt={`preview-${index}`} className="img-thumb" />
                            </div>

                            {/* filename under card, single line with … */}
                            <div className="small text-center mt-1 text-muted text-truncate" title={item.file.name}>
                              {item.file.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {uploadedFiles && uploadedFiles.length > 0 && (
                      <>
                        <div className="row g-3 mt-3">
                          <div className="col-12 mb-1">
                            <h6 className="text-muted">Already Uploaded Images</h6>
                          </div>
                          {(uploadedFiles ?? []).map((file, index) => {
                            const raw = file?.url;
                            const src =
                              // prefer a resolver if parent passed one; else fallback to baseUrl
                              (typeof resolveSrc === "function" ? resolveSrc(raw) : null) ||
                              (typeof raw === "string" && raw.startsWith("http")
                                ? raw
                                : `${baseUrl || process.env.REACT_APP_BASE_URL || ""}/${String(raw || "").replace(
                                  /^https?:\/\/[^/]+\/(uploads\/.*)/,
                                  "$1"
                                )}`);

                            return (
                              <div key={`saved-${index}`} className="col-6 col-md-4 col-lg-3 mb-3">
                                <div className="img-card">
                                  <button
                                    type="button"
                                    className="btn-close img-x"
                                    aria-label="Remove"
                                    onClick={() => onRemoveUploaded(index)}     // ✅ index to parent
                                  />
                                  {src ? (
                                    <img src={src} alt={file?.alt_text || "Image"} className="img-thumb" />
                                  ) : (
                                    <div className="text-muted small">Loading…</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {/* {uploadedFiles.map((file, index) => (
                            <div key={`uploaded-${index}`} className="col-6 col-md-4 col-lg-3">
                              <div className="img-card position-relative">
                                <button
                                  type="button"
                                  className="btn-close img-x"
                                  aria-label="Remove"
                                  onClick={() => onRemoveUploaded(index)}
                                />
                                <img
                                  src={
                                    file.url?.startsWith("http")
                                      ? file.url
                                      : `${process.env.REACT_APP_BASE_URL}/${file.url?.replace(
                                        /^https?:\/\/[^\/]+\/(uploads\/.*)/,
                                        "$1"
                                      )}`
                                  }
                                  alt={file.alt_text}
                                  className="img-thumb"
                                />
                              </div>
                              <div className="small text-center mt-1 text-muted text-truncate">
                                {file.alt_text || `Image ${index + 1}`}
                              </div>
                            </div>
                          ))} */}
                        </div>
                      </>
                    )}


                    {/* New preview images */}
                    {previewFiles.length === 0 && uploadedFiles.length === 0 && (
                      <div className="col-12 text-center text-muted">
                        <i className="bi bi-image fs-2 d-block mb-2" />
                        No images uploaded yet
                      </div>
                    )}

                  </div>
                </div>
              </div>

              {/* RIGHT: Product Details */}
              <div className="col-lg-6">
                <div className="rounded-4 p-3">
                  <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Product Details</h5>

                  <UIInput
                    formik={formik as any}
                    name="name"
                    label="Product Name"
                    required
                    placeholder="Enter product name"
                  />

                  <div className="row g-3">
                    <div className="col-md-8">
                      <UIInput
                        formik={formik as any}
                        name="sku"
                        label="SKU"
                        required={requireSku}
                        placeholder="Enter SKU"
                      />
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <UIInput
                        formik={formik as any}
                        name="qty"
                        label="Quantity"
                        required
                        placeholder="0"
                      />
                    </div>
                    <div className="col-md-6">
                      <UIInput
                        formik={formik as any}
                        name="threshold_qty"
                        label="Threshold Quantity"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">
                      Dimensions/Measurements (inches)
                    </h5>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <UIInput formik={formik as any} name="length" label="Length" placeholder="0.0" />
                      </div>
                      <div className="col-md-4">
                        <UIInput formik={formik as any} name="height" label="Height" placeholder="0.0" />
                      </div>
                      <div className="col-md-4">
                        <UIInput formik={formik as any} name="width" label="Width" placeholder="0.0" />
                      </div>
                      <div className="col-md-12">
                        <UIInput formik={formik as any} name="weight" label="Weight (gms)" placeholder="0.0" />
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-6">
                      <UIReactSelect
                        formik={formik as any}
                        name="item_condition"
                        label="Item Condition"
                        options={conditionOptions}
                        placeholder="Select condition"
                      />
                    </div>
                    <div className="col-md-6">
                      <UIReactSelect
                        formik={formik as any}
                        name="statuss"
                        label="Status"
                        options={statusOptions}
                        placeholder="Select status"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="row mt-4">
              <div className="col-lg-6">
                <div className="rounded-4 p-3">
                  <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Product Description</h5>
                  <div className="fw-semibold mb-2">Description</div>
                  <textarea
                    id="description"
                    name="description"
                    className="form-control product-description-textarea"
                    rows={4}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      borderRadius: 10,
                      background: INPUT_BG,
                      borderColor: BORDER,
                      fontSize: 14,
                    }}
                    placeholder="Enter shipping description (optional)"
                  />
                </div>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-lg-12">
                <div className="rounded-4 p-3">
                  <div className="col-12 mb-2" id="row-overlap">
                    <hr className="ms-4 me-3" />
                  </div>

                  {/* footer */}
                  <div className="d-flex justify-content-between align-items-center ms-1" style={{ padding: "14px 18px" }}>
                    <button
                      type="button"
                      onClick={onBack}
                      className="btn back-btn surface-btn fw-bold fs-16"
                    >
                      <i className="bi bi-chevron-left me-2" />
                      Back
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary save-btn surface-btn fw-bold fs-16"
                      disabled={submitting}
                    >
                      {submitting ? (isEdit ? "Saving..." : "Saving...") : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <style>{`
        .rc-tabs{display:flex;gap:8px;flex-wrap:wrap}
        .rc-tab{border:1px solid #e5e7eb;background:#f8f9fb;padding:8px 14px;border-radius:10px;font-weight:600;color:#374151}
        .rc-tab:hover{background:#eef2ff}

        .imageuploadui{border:2px dashed #d1d5db;border-radius:14px;padding:40px 12px;text-align:center;cursor:pointer;transition:all .2s}
        .imageuploadui:hover{border-color:#6d28d9;background:#fbfaff}
        .uploadedimages{border-radius:10px}
          /* Thumbnail card so all X icons share the same anchor */
.img-card{
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  height: 180px;                 /* same height for all tiles */
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Image fits nicely without changing card height */
.img-thumb{
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 10px;
}

/* Close button perfectly aligned inside the tile */
.img-x{
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 2;
  opacity: 0.9;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,.15);
  padding: 0;
}

/* optional: hover feedback */
.img-card:hover .img-x{ opacity: 1; }

        /* show/hide desktop vs mobile */
        .rdt-desktop{display:block}
        .rdt-mobile{display:none}
        @media (max-width:1021px){
          .rdt-desktop{display:none}
          .rdt-mobile{display:block}
        }
      `}</style>
        </div>
      </form>
    </div>
    // <div className="content container">
    //   <div className="card border rounded-4 shadow-sm overflow-hidden production-card">
    //     {/* Header */}
    //     <div className="d-flex align-items-center p-4">
    //       <span className="d-inline-flex align-items-center justify-content-center me-2" style={{ width: 28, height: 28 }}>
    //         <i className="bi bi-box" style={{ fontSize: 22, color: "#155dfc" }} />
    //       </span>
    //       <div>
    //         <h3 className="m-0 fw-bold addheaders">{title}</h3>
    //         <div className="text-muted mt-1" style={{ fontSize: 14 }}>
    //           Manage product details for your agent’s inventory
    //         </div>
    //       </div>
    //     </div>
    //     <hr className="mb-0" />

    //     <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} noValidate>
    //       <div className="p-4">

    //         {/* Two columns - Basic (left) / Product (right) */}
    //         <div className="row g-4">
    //           {/* Left column */}
    //           <div className="col-lg-6">
    //             <div className="rounded-4 border bg-white shadow-sm p-3 p-md-4">
    //   <div className="d-flex align-items-center mb-3">
    //     <span className="chip chip-pink me-2"><i className="bi bi-info-circle" /></span>
    //     <h6 className="mb-0 fs-19" style={{ color: "#101828" }}>Basic Information</h6>
    //   </div>

    //   <div className="row g-3">
    //     {!isEdit && (
    //       <div className="col-md-12">
    //         <UIReactSelect
    //           formik={formik}
    //           name="agentId"
    //           label="Agent"
    //           required
    //           options={agentOptions}
    //           placeholder="Select agent"
    //         />
    //       </div>
    //     )}

    //     <div className="col-md-12">
    //       <UIInput
    //         formik={formik}
    //         name="name"
    //         label="Product Name"
    //         required
    //         placeholder="Enter product name"
    //       />
    //     </div>

    //     <div className="col-md-6">
    //       <UIInput
    //         formik={formik}
    //         name="sku"
    //         label="SKU"
    //         required
    //         placeholder="Enter SKU"
    //       />
    //     </div>

    //     <div className="col-md-6">
    //       <UIInput
    //         formik={formik}
    //         name="qty"
    //         label="Quantity"
    //         required
    //         placeholder="0"
    //         // type="number"
    //       />
    //     </div>

    //     <div className="col-md-6">
    //       <UIReactSelect
    //         formik={formik}
    //         name="brand"
    //         label="Brand"
    //         required
    //         options={brandOptions}
    //         placeholder="Select brand"
    //       />
    //     </div>

    //     <div className="col-md-6">
    //       <UIReactSelect
    //         formik={formik}
    //         name="category"
    //         label="Category"
    //         required
    //         options={categoryOptions}
    //         placeholder="Select category"
    //       />
    //     </div>
    //   </div>

    //               {/* Images */}
    //               <div className="mt-4">
    //                 <div className="mb-2">
    //                   <h6 className="mb-0 fs-19" style={{ color: "#101828" }}>Images</h6>
    //                 </div>
    //                 <div
    //                   className="d-flex align-items-center justify-content-center rounded-4"
    //                   style={{
    //                     border: "2px dashed #d0d5dd",
    //                     background: "#fafbff",
    //                     height: 180,
    //                   }}
    //                   onDragOver={(e) => e.preventDefault()}
    //                   onDrop={(e) => {
    //                     e.preventDefault();
    //                     const files = Array.from(e.dataTransfer.files || []) as File[];
    //                     onAddFiles(files);
    //                   }}
    //                 >
    //                   <div className="text-center">
    //                     <div className="mb-3">
    //                       <i className="bi bi-cloud-arrow-up" style={{ fontSize: 28 }} />
    //                     </div>
    //                     <button
    //                       type="button"
    //                       className="btn btn-outline-secondary"
    //                       onClick={() => {
    //                         const input = document.createElement("input");
    //                         input.type = "file";
    //                         input.accept = "image/*";
    //                         input.multiple = true;
    //                         input.onchange = () => {
    //                           const files = Array.from(input.files || []) as File[];
    //                           onAddFiles(files);
    //                         };
    //                         input.click();
    //                       }}
    //                     >
    //                       Choose files
    //                     </button>
    //                     <div className="text-muted mt-2" style={{ fontSize: 12 }}>
    //                       PNG, JPG up to 10MB each • You can select multiple images at once
    //                     </div>
    //                   </div>
    //                 </div>

    //                 {/* already uploaded */}
    //                 {uploadedFiles?.length > 0 && (
    //                   <div className="row mt-3">
    //                     {uploadedFiles.map((f, i) => (
    //                       <div key={`uploaded-${i}`} className="col-md-3 position-relative mb-3">
    //                         <div className="border p-2">
    //                           <img
    //                             src={`${process.env.REACT_APP_BASE_URL?.replace(/\/+$/,"/")}${f.url?.replace(/^\/+/, "")}`}
    //                             alt={f.alt_text}
    //                             className="img-fluid uploadedimages"
    //                           />
    //                           <div className="small text-center mt-1 text-muted">{f.alt_text}</div>
    //                         </div>
    //                       </div>
    //                     ))}
    //                   </div>
    //                 )}

    //                 {/* new previews */}
    //                 {previewFiles?.length > 0 && (
    //                   <div className="row mt-1">
    //                     {previewFiles.map((item, index) => (
    //                       <div key={`preview-${index}`} className="col-md-3 position-relative mb-3">
    //                         <div
    //                           className="position-absolute top-0 end-0 bg-danger text-white rounded-circle imagepreview"
    //                           onClick={() => onRemovePreview(index)}
    //                           role="button"
    //                           aria-label="Remove preview"
    //                         >
    //                           &times;
    //                         </div>
    //                         <div className="border p-2">
    //                           <img src={item.url} alt={`preview-${index}`} className="img-fluid uploadedimages" />
    //                           <div className="small text-center mt-1 text-muted">{item.file.name}</div>
    //                         </div>
    //                       </div>
    //                     ))}
    //                   </div>
    //                 )}
    //               </div>

    //               {/* Description */}
    //               <div className="mt-4">
    //                 <UIInput
    //                   formik={formik}
    //                   name="description"
    //                   label="Product Description"
    //                   placeholder="Enter product description..."
    //                 //   as="textarea"
    //                 //   rows={4}
    //                 />
    //               </div>
    //             </div>
    //           </div>

    //           {/* Right column */}
    //           <div className="col-lg-6">
    //             <div className="rounded-4 border bg-white shadow-sm p-3 p-md-4 h-100">
    //               <div className="d-flex align-items-center mb-3">
    //                 <span className="chip chip-pink me-2"><i className="bi bi-box-seam" /></span>
    //                 <h6 className="mb-0 fs-19" style={{ color: "#101828" }}>Product Details</h6>
    //               </div>

    //               <div className="row g-3">
    //                 <div className="col-md-6">
    //                   <UIInput
    //                     formik={formik}
    //                     name="threshold_qty"
    //                     label="Threshold Quantity"
    //                     required
    //                     placeholder="0"
    //                     // type="number"
    //                   />
    //                 </div>
    //                 <div className="col-md-6">
    //                   <UIReactSelect
    //                     formik={formik}
    //                     name="item_condition"
    //                     label="Item Condition"
    //                     required
    //                     options={conditionOptions}
    //                     placeholder="Select condition"
    //                   />
    //                 </div>

    //                 <div className="col-12">
    //                   <label className="form-label table-cells ms-1">Dimensions/Measurements (inches)</label>
    //                   <div className="row g-3">
    //                     <div className="col-md-3">
    //                       <UIInput formik={formik} name="length" label="Length" placeholder="0.0" />
    //                     </div>
    //                     <div className="col-md-3">
    //                       <UIInput formik={formik} name="height" label="Height" placeholder="0.0" />
    //                     </div>
    //                     <div className="col-md-3">
    //                       <UIInput formik={formik} name="width" label="Width" placeholder="0.0" />
    //                     </div>
    //                     <div className="col-md-3">
    //                       <UIInput formik={formik} name="weight" label="Weight (gms)" placeholder="0.0" />
    //                     </div>
    //                   </div>
    //                 </div>

    //                 <div className="col-md-6">
    //                   <UIReactSelect
    //                     formik={formik}
    //                     name="statuss"
    //                     label="Status"
    //                     required
    //                     options={statusOptions}
    //                     placeholder="Select status"
    //                   />
    //                 </div>
    //               </div>
    //             </div>
    //           </div>
    //         </div>

    //         {/* Footer */}
    //         <hr className="mt-4" />
    //         <div className="d-flex justify-content-between">
    //           <button type="button" className="btn btn-outline-secondary br-8 backbutton" onClick={onBack}>
    //             Back
    //           </button>
    //           <button type="submit" className="btn btn-primary submit-btn" disabled={submitting}>
    //             {submitting ? (isEdit ? "Saving..." : "Saving...") : isEdit ? "Save" : "Save"}
    //           </button>
    //         </div>
    //       </div>
    //     </form>
    //   </div>
    // </div>
  );
};

export default AgentProductForm;
